import React from 'react';
import { exportToCSV } from '@/utils/export';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import {
  walletService,
  type TransactionQueryParams,
} from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface TransactionHistoryTableProps {
  userId: string;
  params?: TransactionQueryParams;
}

export function TransactionHistoryTable({
  userId,
  params = {},
}: TransactionHistoryTableProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wallet-transactions', userId, params],
    queryFn: () =>
      walletService.getUserTransactions(userId, {
        ...params,
        maxResultCount: 50,
      }),
  });

  const handleExport = async () => {
    try {
      const allTransactions = await walletService.getUserTransactions(userId, {
        ...params,
        maxResultCount: 10000,
      });
      exportToCSV(
        allTransactions.items.map((t) => ({
          ...t,
          changeTypeName: t.changeType === 1 ? 'Credit' : 'Debit',
          processedAt: format(new Date(t.processedAt), 'yyyy-MM-dd HH:mm:ss'),
          balanceBefore:
            t.balanceBefore ??
            t.balanceAfter - t.amount * (t.changeType === 1 ? -1 : 1),
        })),
        [
          { key: 'processedAt', label: 'Date' },
          { key: 'changeTypeName', label: 'Type' },
          { key: 'amount', label: 'Amount' },
          { key: 'description', label: 'Description' },
          { key: 'category', label: 'Category' },
          { key: 'balanceBefore', label: 'Balance Before' },
          { key: 'balanceAfter', label: 'Balance After' },
          { key: 'createdBy', label: 'Created By' },
        ],
        `transactions_${userId}`,
      );
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (error) {
    return <div className="text-danger p-4">Failed to load transactions</div>;
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse h-12 bg-muted rounded" />
        ))}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No transactions found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {data.totalCount} transaction{data.totalCount !== 1 ? 's' : ''}
        </p>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Balance Before</TableHead>
            <TableHead className="text-right">Balance After</TableHead>
            <TableHead>Created By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell className="text-muted-foreground">
                {format(
                  new Date(transaction.processedAt),
                  'MMM dd, yyyy HH:mm',
                )}
              </TableCell>
              <TableCell>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    transaction.changeType === 1
                      ? 'bg-success-light text-success'
                      : 'bg-danger-light text-danger'
                  }`}
                >
                  {transaction.changeType === 1 ? 'Credit' : 'Debit'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-xs bg-muted px-2 py-1 rounded">
                  {transaction.category}
                </span>
              </TableCell>
              <TableCell className="max-w-xs truncate">
                {transaction.description}
              </TableCell>
              <TableCell
                className={`text-right font-semibold ${
                  transaction.changeType === 1 ? 'text-success' : 'text-danger'
                }`}
              >
                {transaction.changeType === 1 ? '+' : '-'}
                {Math.abs(transaction.amount).toLocaleString()}
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                {(
                  transaction.balanceBefore ??
                  transaction.balanceAfter -
                    transaction.amount * (transaction.changeType === 1 ? -1 : 1)
                ).toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-medium">
                {transaction.balanceAfter.toLocaleString()}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {transaction.createdBy || 'System'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
